import React, { useState, useEffect, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";

import useAgora from "./hooks/useVideoChat";

import DefaultAvatar from "./components/DefaultAvatar";
import MediaPlayer from "./components/MediaPlayer";
import ScreenSharePlayer from "./components/ScreenSharePlayer";
import Chat from "./chat";

import "../videochat/styles.scss";
import "./styles.scss";

const VideoChatPage = () => {
  const { channel } = useParams();
  const history = useHistory();

  const [videoDimensions, setVideoDimensions] = useState({
    height: "200px",
    width: "180px",
    v_panel_width: "",
    v_panel_height: "65vh",
  });

  const {
    client,
    localAudioTrack,
    localVideoTrack,
    toggleVideo,
    remoteUsers,
    joinState,
    setIsMinimized,
    leave,
    screenshare,
    isAudioMuted,
    setIsAudioMuted,
    isVideoMuted,
    error,
    isScreenSharing,
    screenTrack,
  } = useAgora(channel);

  const handleLeaveCall = () => {
    leave();
    history.goBack();
    localStorage.removeItem("channel_id");
  };

  const getDimension = () => {
    let video_panel = document.querySelector("#video-panel");
    let v_width = video_panel.clientWidth - 38;
    setVideoDimensions((prev) => ({ ...prev, v_panel_width: v_width }));
  };

  const toggleAudio = () => {
    if (!localAudioTrack) {
      console.warn("No audio track available");
      return;
    }
    const muted = !isAudioMuted;
    localAudioTrack.setMuted(muted);
    setIsAudioMuted(muted);
  };

  const handleScreenShare = async () => {
    try {
      await screenshare(channel);
    } catch (err) {
      console.error("Screen share failed:", err);
    }
  };

  useEffect(() => {
    getDimension();
    setIsMinimized(false);
  }, []);

  useEffect(() => {
    {
      if (channel) {
        localStorage.setItem("channel_id", JSON.stringify(channel));
      }
    }
  }, [channel]);

  useEffect(() => {
    const videoPanel = document.getElementById("video-panel");
    if (videoPanel) {
      setVideoDimensions((prev) => ({
        ...prev,
        v_panel_width: videoPanel.clientWidth - 38,
      }));
    }
  }, []);

  const MemorizedVideoPlayer = useMemo(() => {
    return function VideoPlayer() {
      return localVideoTrack && localVideoTrack?.enabled ? (
        <MediaPlayer
          videoTrack={localVideoTrack}
          audioTrack={undefined}
          width={
            remoteUsers.length == 1
              ? "140px"
              : videoDimensions.v_panel_width + "px"
          }
          height={
            remoteUsers.length == 1 ? "140px" : videoDimensions.v_panel_height
          }
          local={true}
          id="local-2"
        />
      ) : (
        <DefaultAvatar size="150px" />
      );
    };
  }, [localVideoTrack, remoteUsers, videoDimensions, isVideoMuted]);

  if (error) {
    return (
      <div className="video-chat-error">
        <h2>Call Error</h2>
        <p>{error}</p>
        <button onClick={handleLeaveCall}>Return to Calls</button>
      </div>
    );
  }

  return (
    <div className="call mt-2 pb-3 mb-3" style={{ background: "#f5f5f5" }}>
      <div className="video-chat-container video-container" id="video-panel">
        {!joinState && (
          <div className="call-status">
            <p>Connecting to the call...</p>
          </div>
        )}
        {/* Screen Sharing Video */}
        {isScreenSharing && screenTrack && (
          <div className="screen-share-container">
            <ScreenSharePlayer screenTrack={screenTrack} />
          </div>
        )}
        <div
          className="video video-container"
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            width: "100%",
            justifyContent: "center",
          }}
        >
          {remoteUsers.length > 1 ? (
            <div className="greater-people-wrapper">
              <div className="local-player-wrapper">
                <div className="local-player-text">
                  {joinState && localVideoTrack
                    ? client?._joinInfo?.optionalInfo + "(You)"
                    : ""}
                </div>
                {localVideoTrack && !isVideoMuted ? (
                  <div
                    style={{
                      width: videoDimensions.v_panel_width + "px",
                      height: videoDimensions.v_panel_height,
                    }}
                  >
                    <MediaPlayer
                      videoTrack={localVideoTrack}
                      audioTrack={undefined}
                      width={videoDimensions.v_panel_width + "px"}
                      height={videoDimensions.v_panel_height}
                      local={true}
                      id="local"
                    />
                  </div>
                ) : (
                  <DefaultAvatar
                    size={videoDimensions.v_panel_width + "px"}
                    height={videoDimensions.v_panel_height}
                  />
                )}
              </div>
              <section className="remote-people">
              {remoteUsers.map((user) => {
                return (
                  <div className="remote-player-wrapper-1" key={user.uid}>
                    {!!(!!user["_video_muted_"] && !!user.audioTrack) ? (
                      <>
                        <div style={{ display: "none" }}>
                          <MediaPlayer
                            videoTrack={user.videoTrack}
                            audioTrack={user.audioTrack}
                            width={videoDimensions.v_panel_width + "px"}
                            height={videoDimensions.v_panel_height}
                            id="remote"
                          />
                        </div>
                        <div
                          style={{
                            width: videoDimensions.width,
                            height: videoDimensions.height,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <DefaultAvatar size={videoDimensions.width} />
                        </div>
                      </>
                    ) : !!(user.videoTrack && !user["_video_muted_"]) ||
                      !!user.audioTrack ? (
                      <MediaPlayer
                        videoTrack={user.videoTrack}
                        audioTrack={user.audioTrack}
                        width={"200px"}
                        height={"200px"}
                        id="remote"
                      />
                    ) : (
                      <DefaultAvatar size={videoDimensions.width} />
                    )}
                  </div>
                );
              })}
              </section>
            </div>
          ) : (
            <>
              <div
                className={`local-player-wrapper-1 ${remoteUsers.length == 1 ? "localcss" : ""
                  }`}
              >
                <MemorizedVideoPlayer />
              </div>
              {remoteUsers.map((user) => {
                return (
                  <div className="remote-player-wrapper" key={user.uid}>
                    {!!(!!user["_video_muted_"] && !!user.audioTrack) ? (
                      <>
                        <div style={{ display: "none" }}>
                          <MediaPlayer
                            videoTrack={user.videoTrack}
                            audioTrack={user.audioTrack}
                            width={videoDimensions.v_panel_width + "px"}
                            height={videoDimensions.v_panel_height}
                            id="remote"
                          />
                        </div>
                        <div
                          style={{
                            width: videoDimensions.width,
                            height: videoDimensions.height,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <DefaultAvatar size={videoDimensions.width} />
                        </div>
                      </>
                    ) : !!(user.videoTrack && !user["_video_muted_"]) ||
                      !!user.audioTrack ? (
                      <MediaPlayer
                        videoTrack={user.videoTrack}
                        audioTrack={user.audioTrack}
                        width={videoDimensions.v_panel_width + "px"}
                        height={videoDimensions.v_panel_height}
                        id="remote"
                      />
                    ) : (
                      <DefaultAvatar size={videoDimensions.width} />
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div className="video-controls">
          <button
            onClick={toggleAudio}
            className={`audio-toggle ${isAudioMuted ? "muted" : "active"}`}
          >
            <i
              className={`fas ${
                isAudioMuted ? "fa-microphone-slash" : "fa-microphone"
              }`}
            ></i>
            {isAudioMuted ? "Unmute" : "Mute"}
          </button>
          <button
            onClick={toggleVideo}
            className={`video-toggle ${isVideoMuted ? "muted" : "active"}`}
          >
            <i
              className={`fas ${isVideoMuted ? "fa-video-slash" : "fa-video"}`}
            ></i>
            {isVideoMuted ? "Show Video" : "Hide Video"}
          </button>

          {/* Screen Share */}
          <button
            onClick={handleScreenShare}
            className={`screen-share ${isScreenSharing ? "active" : "muted"}`}
          >
            <i className={`fas fa-desktop`}></i>
            {isScreenSharing ? "Stop Sharing" : "Share Screen"}
          </button>

          {/* Leave Call */}
          <button onClick={handleLeaveCall} className="leave-call">
            <i className="fas fa-phone-slash"></i>
            Leave Call
          </button>
        </div>
      </div>

      {joinState && client && (
        <div
          className="chat-container col-md-4 col-xs-5"
          id="chat-panel"
          style={{ paddingLeft: "0px" }}
        >
          <Chat
            clientId={client.uid}
            channel={{
              name: channel,
              clientname: localStorage.getItem("loggedInUser"),
            }}
          />
        </div>
      )}
    </div>
  );
};

export default VideoChatPage;
